

























































import { Component, Prop, Vue, Watch } from "@/shared/vuepack";
import { Location } from "vue-router";
import Terms from "./Terms.vue";
import ContactInfo from "./ContactInfo.vue";
import Investments from "./Investments.vue";
import IndividualPlanDetails from "./IndividualPlanDetails.vue";
import Family from "./Family.vue";
import YourHsa from "./YourHsa.vue";
import VerifyIdentity from "./VerifyIdentity.vue";
import Finish from "./Finish.vue";
import { UserOnboardingStepKey, UserOnboardingSteps, UserTermsType } from "@/shared/types";
import WizardItem from "@/shared/components/WizardItem.vue";
import ClaimAccountTerms from "@/employee/components/claimAccountOnboarding/ClaimAccountTerms.vue";
import ClaimAccountPersona from "@/employee/components/claimAccountOnboarding/ClaimAccountPersona.vue";
import ClaimAccountComplete from "@/employee/components/claimAccountOnboarding/ClaimAccountComplete.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import * as UserOnboarding from "@/shared/userOnboarding";
import { useWizardStore } from "@/employee/store/wizard";
import { useUserStore } from "@/shared/store/user";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { BankCustodian } from "@/admin/types";
import FinishV2 from "./FinishV2.vue";
import { usePortalCustomizationStore } from "@/shared/store/portalCustomization";
import * as HsaBankConstants from "@/shared/hsabConstants";
import HsaBankOnboardingFooter from "@/employee/components/shared/HsaBankOnboardingFooter.vue";
import ProgressIndicator from "@/shared/components/ProgressIndicator.vue";
import * as RouteNames from "@/employee/route-names";
import UnsavedChangesPrompt from "@/employee/components/onboarding/UnsavedChangesPrompt.vue";

@Component({
  components: {
    HsaBankOnboardingFooter,
    terms: Terms,
    you: ContactInfo,
    yourPlan: IndividualPlanDetails,
    yourhsa: YourHsa,
    family: Family,
    finish: Finish,
    wizarditem: WizardItem,
    ClaimAccountTerms,
    ClaimAccountPersona,
    ClaimAccountComplete,
    GenericLoadingSpinner,
    VerifyIdentity,
    Investments,
    FinishV2,
    ProgressIndicator,
    UnsavedChangesPrompt
  }
})
export default class Wizard extends Vue {
  wizardStore = useWizardStore();
  userStore = useUserStore();
  custodianSettingsStore = useCustodianSettingsStore();
  portalCustomizationStore = usePortalCustomizationStore();
  UserTermsType = UserTermsType;

  @Prop()
  stepKey: UserOnboardingStepKey;

  @Prop()
  goToStep: (step: UserOnboardingStepKey) => Location;

  @Prop()
  routeToFinish: () => Location;

  @Prop()
  routeToWizardComplete: () => Location;

  // If the pre-enrollment flags change while on the investments step, redirect to the new max allowed step
  @Watch("wizardStore.isDrivewealthPreenrollmentAllowed")
  redirectFromInvestments () {
    if (this.idOfCurrentStep === UserOnboardingSteps.Investments) {
      this.$router.push(this.goToStep(this.wizardStore.maxAllowedStepKey));
    }
  }

  onStepComplete () {
    this.wizardStore.setStepKeyComplete(this.stepKey)
        .then(() => this.$router.push(this.goToStep(this.wizardStore.maxAllowedStepKey)));
  }

  onStepCompleteYou () {
    this.wizardStore.setStepKeyComplete(this.stepKey)
        .then(_ => this.wizardStore.transitionEmployee()) // will switch individual to employee when needed
        .then(_ => this.$router.push(this.goToStep(this.wizardStore.maxAllowedStepKey)));
  }

  onIsFamilyPlan (isFamilyPlan: boolean) {
    this.wizardStore.setIsFamilyPlan(isFamilyPlan);
  }

  created () {
    if (this.userStore.userOnboarded) { // No returning to wizard after onboarding
      this.$router.push(this.routeToWizardComplete());
    } else {
      this.wizardStore.getWizardState();
    }
  }

  get isHsaBank () {
    return this.custodianSettingsStore.userHsaCustodian === BankCustodian.HsaBank;
  }

  get idOfCurrentStep () {
    return UserOnboarding.stepFromKey(this.stepKey);
  }

  get showTerms () {
    return this.idOfCurrentStep === UserOnboardingSteps.Terms;
  }

  get showYou () {
    return this.idOfCurrentStep === UserOnboardingSteps.You;
  }

  get showYourPlan () {
    return this.idOfCurrentStep === UserOnboardingSteps.YourPlan;
  }

  get showYourHsa () {
    return this.idOfCurrentStep === UserOnboardingSteps.YourHsa;
  }

  get showYourFamily () {
    return this.idOfCurrentStep === UserOnboardingSteps.YourFamily;
  }

  get showInvestments () {
    return this.idOfCurrentStep === UserOnboardingSteps.Investments;
  }

  get showVerifyIdentity () {
    return this.idOfCurrentStep === UserOnboardingSteps.VerifyIdentity;
  }

  get showFinish () {
    return this.wizardStore.isComplete || this.idOfCurrentStep === UserOnboardingSteps.Finish;
  }

  get showClaimAccountTerms () {
    return this.idOfCurrentStep === UserOnboardingSteps.ClaimAccountTerms;
  }

  get showClaimAccountPersona () {
    return this.idOfCurrentStep === UserOnboardingSteps.ClaimAccountPersona;
  }

  get showClaimAccountFinish () {
    return this.idOfCurrentStep === UserOnboardingSteps.ClaimAccountFinished;
  }

  get showLoadingSpinner () {
    return this.showYourHsa && !this.wizardStore.isLoaded;
  }

  get displayOnboardingFooter (): boolean {
    return this.portalCustomizationStore.partnerId === HsaBankConstants.HsaBankRetailPartnerId;
  }

  get pageTitle () {
    switch (this.idOfCurrentStep) {
      case UserOnboardingSteps.You:
        return "Personal details";
      case UserOnboardingSteps.YourPlan:
        return "Health plan";
      case UserOnboardingSteps.Terms:
        return "Terms and conditions";
      case UserOnboardingSteps.YourFamily:
        return "Dependent details";
      case UserOnboardingSteps.VerifyIdentity:
        return "Review your information";
      default:
        return undefined;
    }
  }

  cssClassName (stepKey: UserOnboardingStepKey) {
    if (this.stepKey === stepKey) {
      return "current";
    }

    return this.wizardStore.isStepComplete(stepKey) ? "done" : "todo";
  }

  get selectedStepIndex (): number {
    if (this.stepKey) {
      return this.wizardStore.onboardingStepIndex(this.stepKey);
    }
    return this.selectedStepIndex;
  }

  get progressBarItems (): string[] {
    return this.wizardStore.shownStatuses.map(s => s.label);
  }

  get itemLink (): (index: number) => string | Location {
    if (!this.wizardStore.shownStatuses || this.wizardStore.shownStatuses.length === 0) {
      return null;
    }
    return index => {
      const stepKey = this.wizardStore.shownStatuses[index].stepKey;
      return {
        name: RouteNames.NamedOnboarding,
        params: {
          stepKey
        }
      };
    };
  }

  onBack (hasUnsavedChanges: boolean) {
    if (hasUnsavedChanges) {
      this.wizardStore.confirmUnsavedChanges();
    } else {
      this.goBack();
    }
  }

  goBack () {
    const indexOfCurrentStep = this.wizardStore.shownStatuses
      .findIndex(s => s.step === this.idOfCurrentStep);

    if (indexOfCurrentStep < 1) {
      return;
    }

    const previousStep = this.wizardStore.shownStatuses[indexOfCurrentStep - 1];

    this.$router.push(this.goToStep(previousStep.stepKey));
  }
}
